import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { API_URL } from '../config/api';
import { TurfPulseLogoSVG } from '../components/TurfPulseLogo';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft, Moon, Sun, Plus, Users, MapPin, Tag, ChatCircle,
  MegaphoneSimple, Handshake, Warning, CaretDown, CaretUp,
  PaperPlaneTilt, X, Check, ShieldWarning, Funnel, Bell
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const LISTING_TYPE_LABELS = {
  offer: { label: 'Offering', color: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' },
  request: { label: 'Looking For', color: 'bg-blue-500/15 text-blue-700 dark:text-blue-400' },
};

const STATUS_LABELS = {
  open: { label: 'Open', color: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  closed: { label: 'Closed', color: 'bg-red-500/15 text-red-600 dark:text-red-400' },
  expired: { label: 'Expired', color: 'bg-gray-500/15 text-gray-600 dark:text-gray-400' },
};

// ── Waiver Dialog ──
const WaiverDialog = ({ open, onAccept, onCancel, disclaimer }) => (
  <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-base">
          <ShieldWarning size={22} className="text-amber-500" />
          Community Marketplace Disclaimer
        </DialogTitle>
      </DialogHeader>
      <div className="mt-2 text-sm text-muted-foreground leading-relaxed bg-muted/50 p-4 rounded-lg border border-border/50">
        {disclaimer}
      </div>
      <div className="flex gap-3 mt-4 justify-end">
        <Button variant="outline" size="sm" onClick={onCancel} data-testid="waiver-cancel">Cancel</Button>
        <Button size="sm" onClick={onAccept} data-testid="waiver-accept">
          <Check size={16} className="mr-1" /> I Understand & Accept
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

// ── Create Listing Dialog ──
const CreateListingDialog = ({ open, onClose, categories, disclaimer, onCreated }) => {
  const [form, setForm] = useState({
    title: '', description: '', category: '', type: 'offer',
    price_info: '', min_participants: 2, max_participants: '',
    location_info: '', contact_method: '',
  });
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!form.title || !form.description || !form.category) {
      toast.error('Title, description, and category are required');
      return;
    }
    if (!acknowledged) {
      toast.error('You must acknowledge the guidelines to post');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        min_participants: parseInt(form.min_participants) || 2,
        max_participants: form.max_participants ? parseInt(form.max_participants) : null,
        waiver_accepted: true,
      };
      const res = await fetch(`${API_URL}/api/marketplace/listings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create listing');
      }
      const data = await res.json();
      toast.success('Listing posted!');
      onCreated(data);
      onClose();
      setForm({ title: '', description: '', category: '', type: 'offer', price_info: '', min_participants: 2, max_participants: '', location_info: '', contact_method: '' });
      setAcknowledged(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  return (
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">Post a Group Buy</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Guidelines */}
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 space-y-1.5" data-testid="create-guidelines">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">Group Buy Guidelines</p>
              <ul className="text-[11px] text-amber-700/80 dark:text-amber-400/80 space-y-1 list-disc pl-4">
                <li>All transactions are between members — TurfPulse is not involved in payments, shipping, or disputes</li>
                <li><strong>Chemical products (PGR, herbicides, fungicides, insecticides, pre-emergents) are not permitted</strong> due to federal and state pesticide regulations</li>
                <li>Allowed categories: Fertilizer, Soil Amendment, Seed, Equipment, Sprayer Parts, Mower Parts</li>
                <li>Be specific about product name, quantity, split cost, and pickup/shipping details</li>
                <li>Include a preferred contact method so participants can coordinate directly</li>
                <li>Listings expire after 30 days — you can renew if the deal is still active</li>
              </ul>
            </div>

            {/* Type */}
            <div className="flex gap-2">
              {['offer', 'request'].map(t => (
                <button
                  key={t}
                  onClick={() => updateField('type', t)}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-all
                    ${form.type === t
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card border-border text-muted-foreground hover:border-primary/30'
                    }`}
                  data-testid={`create-type-${t}`}
                >
                  {t === 'offer' ? 'I Have Product to Split' : "I'm Looking for a Group Buy"}
                </button>
              ))}
            </div>

            {/* Category */}
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Category</Label>
              <Select value={form.category} onValueChange={(v) => updateField('category', v)}>
                <SelectTrigger data-testid="create-category">
                  <SelectValue placeholder="Select product category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Title</Label>
              <Input
                placeholder="e.g., Splitting a case of Primo Maxx 2.5 gal"
                value={form.title}
                onChange={(e) => updateField('title', e.target.value)}
                data-testid="create-title"
              />
            </div>

            {/* Description */}
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Description</Label>
              <textarea
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[80px] resize-y"
                placeholder="Details about the product, quantity, how you want to split, timeline, etc."
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
                data-testid="create-description"
              />
            </div>

            {/* Price Info */}
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Price / Split Info</Label>
              <Input
                placeholder="e.g., $180 total, ~$45/person for 4-way split"
                value={form.price_info}
                onChange={(e) => updateField('price_info', e.target.value)}
                data-testid="create-price"
              />
            </div>

            {/* Participants */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Min People Needed</Label>
                <Input
                  type="number" min="2"
                  value={form.min_participants}
                  onChange={(e) => updateField('min_participants', e.target.value)}
                  data-testid="create-min-participants"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Max People (optional)</Label>
                <Input
                  type="number" min="2"
                  placeholder="No limit"
                  value={form.max_participants}
                  onChange={(e) => updateField('max_participants', e.target.value)}
                  data-testid="create-max-participants"
                />
              </div>
            </div>

            {/* Location */}
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Location / Shipping Info</Label>
              <Input
                placeholder="e.g., Honolulu, HI — local pickup preferred"
                value={form.location_info}
                onChange={(e) => updateField('location_info', e.target.value)}
                data-testid="create-location"
              />
            </div>

            {/* Contact */}
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Preferred Contact Method</Label>
              <Input
                placeholder="e.g., DM on TurfPulse, text, email"
                value={form.contact_method}
                onChange={(e) => updateField('contact_method', e.target.value)}
                data-testid="create-contact"
              />
            </div>

            {/* Acknowledge */}
            <label className="flex items-start gap-2.5 cursor-pointer p-3 rounded-lg border border-border/50 hover:border-primary/30 transition-colors" data-testid="create-acknowledge">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="mt-0.5 rounded border-border accent-primary"
              />
              <span className="text-xs text-muted-foreground leading-relaxed">
                I have read and agree to the <a href="/marketplace-terms" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:text-primary/80">Marketplace Terms of Use</a>. I understand that TurfPulse is not involved in any transactions and that all payments, shipping, logistics, regulatory compliance, and dispute resolution are my sole responsibility.
              </span>
            </label>

            <Button className="w-full" onClick={handleSubmit} disabled={submitting || !acknowledged} data-testid="create-submit">
              {submitting ? 'Posting...' : 'Post Listing'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
  );
};

// ── Listing Detail Dialog ──
const ListingDetailDialog = ({ listing, open, onClose, currentUserId, onJoin, onLeave, onClose_, onRenew, onComment, disclaimer }) => {
  const [commentText, setCommentText] = useState('');
  const [showWaiver, setShowWaiver] = useState(false);
  const [sending, setSending] = useState(false);

  if (!listing) return null;

  const isOrganizer = listing.organizer_id === currentUserId;
  const isParticipant = listing.participants?.some(p => p.user_id === currentUserId);
  const isOpen = listing.status === 'open';
  const isExpired = listing.status === 'expired';
  const typeCfg = LISTING_TYPE_LABELS[listing.type] || LISTING_TYPE_LABELS.offer;
  const statusCfg = STATUS_LABELS[listing.status] || STATUS_LABELS.open;

  const daysLeft = listing.expires_at
    ? Math.max(0, Math.ceil((new Date(listing.expires_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  const handleJoinClick = () => setShowWaiver(true);
  const handleJoinConfirm = () => {
    setShowWaiver(false);
    onJoin(listing.id);
  };

  const handleSendComment = async () => {
    if (!commentText.trim()) return;
    setSending(true);
    await onComment(listing.id, commentText.trim());
    setCommentText('');
    setSending(false);
  };

  return (
    <>
      <Dialog open={open && !showWaiver} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base pr-8 leading-tight">{listing.title}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${typeCfg.color}`}>
                {typeCfg.label}
              </span>
              <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${statusCfg.color}`}>
                {statusCfg.label}
              </span>
              <span className="text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                {listing.category}
              </span>
            </div>

            {/* Description */}
            <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{listing.description}</p>

            {/* Details grid */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              {listing.price_info && (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Tag size={14} /> <span>{listing.price_info}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Users size={14} />
                <span>{listing.participant_count || 0}{listing.max_participants ? `/${listing.max_participants}` : ''} joined (min {listing.min_participants})</span>
              </div>
              {listing.location_info && (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <MapPin size={14} /> <span>{listing.location_info}</span>
                </div>
              )}
              {listing.contact_method && (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <ChatCircle size={14} /> <span>{listing.contact_method}</span>
                </div>
              )}
            </div>

            <div className="text-xs text-muted-foreground">
              Posted by <strong>{listing.organizer_name}</strong> &middot; {new Date(listing.created_at).toLocaleDateString()}
            </div>

            {/* Participants */}
            {listing.participants && listing.participants.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Participants ({listing.participants.length})</p>
                <div className="flex flex-wrap gap-1.5">
                  {listing.participants.map((p, i) => (
                    <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      {p.name}{p.user_id === listing.organizer_id ? ' (organizer)' : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Expiry Info */}
            {listing.expires_at && (
              <div className={`text-xs px-3 py-2 rounded-lg border ${daysLeft <= 5 ? 'bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400' : 'bg-muted/50 border-border/30 text-muted-foreground'}`}>
                {listing.status === 'expired' ? 'This listing has expired.' :
                  daysLeft === 0 ? 'Expires today' :
                  `Expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`
                }
                {listing.renewed_count > 0 && ` (renewed ${listing.renewed_count}x)`}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 flex-wrap">
              {isOpen && !isParticipant && (
                <Button size="sm" className="flex-1" onClick={handleJoinClick} data-testid="detail-join-btn">
                  <Handshake size={16} className="mr-1" /> Join Group Buy
                </Button>
              )}
              {isOpen && isParticipant && !isOrganizer && (
                <Button size="sm" variant="outline" className="flex-1" onClick={() => onLeave(listing.id)} data-testid="detail-leave-btn">
                  Leave Group
                </Button>
              )}
              {isOpen && isOrganizer && (
                <Button size="sm" variant="destructive" onClick={() => onClose_(listing.id)} data-testid="detail-close-btn">
                  Close Listing
                </Button>
              )}
              {isExpired && isOrganizer && (
                <Button size="sm" className="flex-1 bg-amber-500 hover:bg-amber-600 text-black" onClick={() => onRenew(listing.id)} data-testid="detail-renew-btn">
                  Renew for 30 Days
                </Button>
              )}
              {!isOpen && !isExpired && (
                <div className="flex-1 text-center py-2 text-sm text-muted-foreground italic">This listing is closed</div>
              )}
            </div>

            {/* Disclaimer */}
            <div className="text-[10px] text-muted-foreground/60 leading-relaxed border-t border-border/30 pt-3">
              <Warning size={12} className="inline mr-1" />
              {disclaimer}
            </div>

            {/* Comments */}
            <div className="border-t border-border/30 pt-4">
              <p className="text-xs font-medium text-muted-foreground mb-3">
                Discussion ({listing.comments?.length || 0})
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
                {(listing.comments || []).map(c => (
                  <div key={c.id} className="text-xs bg-muted/40 rounded-lg p-2.5">
                    <span className="font-medium text-foreground">{c.user_name}</span>
                    <span className="text-muted-foreground ml-2">{new Date(c.created_at).toLocaleString()}</span>
                    <p className="mt-1 text-foreground/80">{c.text}</p>
                  </div>
                ))}
                {(!listing.comments || listing.comments.length === 0) && (
                  <p className="text-xs text-muted-foreground italic">No comments yet. Start the conversation.</p>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a comment..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendComment()}
                  className="text-xs"
                  data-testid="detail-comment-input"
                />
                <Button size="sm" variant="ghost" onClick={handleSendComment} disabled={sending} data-testid="detail-comment-send">
                  <PaperPlaneTilt size={16} />
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <WaiverDialog
        open={showWaiver}
        disclaimer={disclaimer}
        onAccept={handleJoinConfirm}
        onCancel={() => setShowWaiver(false)}
      />
    </>
  );
};

// ── Listing Card ──
const ListingCard = ({ listing, onClick }) => {
  const typeCfg = LISTING_TYPE_LABELS[listing.type] || LISTING_TYPE_LABELS.offer;
  const statusCfg = STATUS_LABELS[listing.status] || STATUS_LABELS.open;
  const progress = listing.max_participants
    ? Math.min((listing.participant_count / listing.max_participants) * 100, 100)
    : null;

  // Calculate days remaining
  const daysLeft = listing.expires_at
    ? Math.max(0, Math.ceil((new Date(listing.expires_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <Card
      className="group cursor-pointer border border-border/40 hover:border-primary/30 hover:shadow-md transition-all duration-200 bg-card"
      onClick={onClick}
      data-testid={`listing-card-${listing.id}`}
    >
      <CardContent className="p-4 sm:p-5">
        {/* Top badges */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${typeCfg.color}`}>
            {typeCfg.label}
          </span>
          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${statusCfg.color}`}>
            {statusCfg.label}
          </span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">
            {listing.category}
          </span>
        </div>

        {/* Title */}
        <h3 className="font-semibold text-sm sm:text-base text-foreground leading-tight mb-1 group-hover:text-primary transition-colors">
          {listing.title}
        </h3>

        {/* Description preview */}
        <p className="text-xs text-muted-foreground line-clamp-2 mb-3 leading-relaxed">
          {listing.description}
        </p>

        {/* Meta row */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
          <span className="flex items-center gap-1">
            <Users size={13} />
            {listing.participant_count || 0}{listing.max_participants ? `/${listing.max_participants}` : ''} joined
          </span>
          {listing.price_info && (
            <span className="flex items-center gap-1">
              <Tag size={13} /> {listing.price_info}
            </span>
          )}
          {listing.location_info && (
            <span className="flex items-center gap-1">
              <MapPin size={13} /> {listing.location_info}
            </span>
          )}
        </div>

        {/* Progress bar */}
        {progress !== null && listing.status === 'open' && (
          <div className="mt-3">
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-3 flex items-center justify-between text-[10px] text-muted-foreground/70">
          <span>by {listing.organizer_name}</span>
          <div className="flex items-center gap-2">
            {daysLeft !== null && listing.status === 'open' && (
              <span className={daysLeft <= 5 ? 'text-amber-500 font-medium' : ''}>
                {daysLeft}d left
              </span>
            )}
            <span>{new Date(listing.created_at).toLocaleDateString()}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ── Main Page ──
export const MarketplacePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [disclaimer, setDisclaimer] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  // Filters
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('open');

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/marketplace/categories`, { credentials: 'include' });
      const data = await res.json();
      setCategories(data.categories || []);
      setDisclaimer(data.disclaimer || '');
    } catch { /* ignore */ }
  }, []);

  const fetchListings = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterCategory !== 'all') params.append('category', filterCategory);
      if (filterType !== 'all') params.append('listing_type', filterType);
      if (filterStatus !== 'all') params.append('status', filterStatus);

      const res = await fetch(`${API_URL}/api/marketplace/listings?${params}`, { credentials: 'include' });
      const data = await res.json();
      setListings(data.listings || []);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, [filterCategory, filterType, filterStatus]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchListings(); }, [fetchListings]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/marketplace/notifications`, { credentials: 'include' });
      const data = await res.json();
      setNotifications(data.notifications || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const openDetail = async (listing) => {
    try {
      const res = await fetch(`${API_URL}/api/marketplace/listings/${listing.id}`, { credentials: 'include' });
      const full = await res.json();
      setSelectedListing(full);
      setShowDetail(true);
    } catch { toast.error('Failed to load listing'); }
  };

  const handleJoin = async (listingId) => {
    try {
      const res = await fetch(`${API_URL}/api/marketplace/listings/${listingId}/join`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ waiver_accepted: true }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      toast.success("You're in! Coordinate with the organizer.");
      openDetail({ id: listingId });
      fetchListings();
    } catch (e) { toast.error(e.message); }
  };

  const handleLeave = async (listingId) => {
    try {
      const res = await fetch(`${API_URL}/api/marketplace/listings/${listingId}/leave`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      toast.info('Left the group buy.');
      openDetail({ id: listingId });
      fetchListings();
    } catch (e) { toast.error(e.message); }
  };

  const handleCloseListing = async (listingId) => {
    try {
      await fetch(`${API_URL}/api/marketplace/listings/${listingId}/close`, {
        method: 'PUT', credentials: 'include',
      });
      toast.success('Listing closed.');
      setShowDetail(false);
      fetchListings();
    } catch { toast.error('Failed to close listing.'); }
  };

  const handleRenew = async (listingId) => {
    try {
      const res = await fetch(`${API_URL}/api/marketplace/listings/${listingId}/renew`, {
        method: 'PUT', credentials: 'include',
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      const data = await res.json();
      toast.success(`Renewed! Expires ${new Date(data.expires_at).toLocaleDateString()}`);
      openDetail({ id: listingId });
      fetchListings();
    } catch (e) { toast.error(e.message); }
  };

  const handleMarkRead = async () => {
    try {
      await fetch(`${API_URL}/api/marketplace/notifications/read`, {
        method: 'PUT', credentials: 'include',
      });
      setNotifications([]);
      setShowNotifications(false);
    } catch { /* ignore */ }
  };

  const handleComment = async (listingId, text) => {
    try {
      const res = await fetch(`${API_URL}/api/marketplace/listings/${listingId}/comments`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error();
      openDetail({ id: listingId });
    } catch { toast.error('Failed to post comment.'); }
  };

  const handleCreated = (newListing) => {
    setListings(prev => [newListing, ...prev]);
  };

  const currentUserId = user?._id || user?.id;

  return (
    <div className="min-h-screen bg-background" data-testid="marketplace-page">
      {/* Header */}
      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="marketplace-back-btn">
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <TurfPulseLogoSVG animated size={28} />
              <span className="font-semibold text-base tracking-tight hidden sm:inline">TurfPulse</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => { setShowNotifications(!showNotifications); }}
                data-testid="marketplace-notifications-btn"
              >
                <Bell size={18} />
                {notifications.length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                    {notifications.length}
                  </span>
                )}
              </Button>
              {showNotifications && (
                <div className="absolute right-0 top-10 w-72 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden" data-testid="marketplace-notifications-dropdown">
                  <div className="p-3 border-b border-border flex items-center justify-between">
                    <span className="text-xs font-semibold">Notifications</span>
                    {notifications.length > 0 && (
                      <button onClick={handleMarkRead} className="text-[10px] text-primary hover:underline">Mark all read</button>
                    )}
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <p className="p-4 text-xs text-muted-foreground text-center">No new notifications</p>
                    ) : (
                      notifications.map(n => (
                        <div
                          key={n.id}
                          className="p-3 border-b border-border/30 hover:bg-muted/30 cursor-pointer"
                          onClick={() => { openDetail({ id: n.listing_id }); setShowNotifications(false); }}
                        >
                          <p className="text-xs text-foreground">{n.message}</p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="marketplace-theme-toggle">
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-amber-950/40 to-slate-900" />
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(245,158,11,0.4) 0%, transparent 50%), radial-gradient(circle at 70% 50%, rgba(16,185,129,0.3) 0%, transparent 50%)' }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16 text-center">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-400 bg-amber-400/10 px-3 py-1 rounded-full border border-amber-400/20">
              Community Marketplace
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight">
            Group Buys for <span className="text-amber-400">Lawn Pros</span>
          </h1>
          <p className="mt-3 text-sm sm:text-base text-slate-400 max-w-xl mx-auto leading-relaxed">
            Split bulk products with the community. Post what you have or what you need.
            All deals arranged between members — TurfPulse is just the bulletin board.
          </p>
          <Button
            className="mt-6 bg-amber-500 hover:bg-amber-600 text-black font-semibold px-6"
            onClick={() => setShowCreate(true)}
            data-testid="marketplace-post-btn"
          >
            <Plus size={18} className="mr-1.5" /> Post a Group Buy
          </Button>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="marketplace-filters">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Funnel size={14} /> Filters:
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="filter-category">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="filter-type">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="offer">Offering</SelectItem>
              <SelectItem value="request">Looking For</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[120px] h-8 text-xs" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Listings Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : listings.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="marketplace-listings-grid">
            {listings.map(l => (
              <ListingCard key={l.id} listing={l} onClick={() => openDetail(l)} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20" data-testid="marketplace-empty">
            <MegaphoneSimple size={48} className="mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-1">No listings yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Be the first to post a group buy for the community.</p>
            <Button onClick={() => setShowCreate(true)} data-testid="marketplace-empty-post-btn">
              <Plus size={16} className="mr-1" /> Post a Group Buy
            </Button>
          </div>
        )}
      </main>

      {/* Dialogs */}
      <CreateListingDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        categories={categories}
        disclaimer={disclaimer}
        onCreated={handleCreated}
      />

      <ListingDetailDialog
        listing={selectedListing}
        open={showDetail}
        onClose={() => setShowDetail(false)}
        currentUserId={currentUserId}
        onJoin={handleJoin}
        onLeave={handleLeave}
        onClose_={handleCloseListing}
        onRenew={handleRenew}
        onComment={handleComment}
        disclaimer={disclaimer}
      />
    </div>
  );
};
